import { registerOverlay } from 'klinecharts'
import { isNum, resolvePricePrecision, type OverlayFigure } from './overlayUtils'
import { type OverlayTemplateParam, registerOnce } from './registrationUtils'
import { buildHorizontalLineAxisFigures } from './horizontalLineOverlay'
import { CHART_COLORS } from './chartColors'
import {
  ICON_CHIP_FIGURE,
  LUCIDE_PLUS_PATHS,
  LUCIDE_X_PATHS,
  registerIconChipFigure,
} from './iconChipFigure'
import {
  computePlanSizing,
  type PlanEntry,
  type PlanRiskType,
  type PlanSizing,
} from './positionSizing'

/**
 * Position Plan — the two-click sizing tool.
 *
 * Gesture is the measure tool's (totalStep 3): first click places the ENTRY,
 * the drag/second click places the STOP. Unlike the measure ruler it persists
 * like any drawing. Direction is never configured — stop below the entries is
 * a long plan, above is a short.
 *
 * Points model: index 1 is ALWAYS the stop; every other index is an entry
 * (0 first, then appended 2, 3, ...). `totalStep` only governs the click
 * gesture — the settings drawer grows the array with
 * `ChartManager.overrideOverlayPoints` (the expandPositionOverlay precedent).
 *
 * Per-drawing config (risk type/value, max position, allocation weights) lives
 * in `extendData` (PositionPlanConfig) and persists via SavedDrawing. Parsing
 * is defaults-on-garbage so a freshly drawn plan sizes immediately with zero
 * configuration — that is the tool's whole point.
 *
 * Every line is an `anchor_<n>`-keyed CUSTOM figure: the vendored klinecharts
 * patch routes drags on anchor-keyed figures to per-point resize, so dragging
 * a line moves only that price (see riskRewardOverlay's contract comment).
 */

export const POSITION_PLAN_NAME = 'positionPlan'

/**
 * Selection resolvers — the riskRewardOverlay registry pattern, but CLICK-only
 * (never hover): selection is what flips the drag semantics from
 * move-one-line to move-the-whole-plan, and a hover-based flip would hijack
 * every per-line drag. ChartManager registers one resolver per chart.
 */
type PlanSelectionResolver = (overlayId: string) => boolean
const planSelectionResolvers = new Set<PlanSelectionResolver>()

export function registerPlanSelectionResolver(fn: PlanSelectionResolver): () => void {
  planSelectionResolvers.add(fn)
  return () => planSelectionResolvers.delete(fn)
}

function isPlanSelected(overlayId: string): boolean {
  for (const fn of planSelectionResolvers) if (fn(overlayId)) return true
  return false
}

/** Hard cap on entries — a plan is a scale-in sketch, not a ladder builder. */
export const MAX_PLAN_ENTRIES = 5

export interface PositionPlanConfig {
  riskType: PlanRiskType
  riskValue: number
  /** Ceiling on capital deployed; 0 = no cap. */
  maxPositionDollars: number
  /** Allocation %, aligned with ENTRY ORDER (point index 0, then 2, 3, ...). */
  weights: number[]
  /** Per-line price pills on the right price axis (like a horizontal line's
   *  label). On by default; the settings drawer can deactivate them. */
  showAxisLabels: boolean
}

export const DEFAULT_PLAN_CONFIG: PositionPlanConfig = {
  riskType: 'dollars',
  riskValue: 100,
  maxPositionDollars: 0,
  weights: [],
  showAxisLabels: true,
}

/** Defaults-on-garbage parse — old or missing payloads must never break render. */
export function parsePlanConfig(extendData: unknown): PositionPlanConfig {
  const raw = (extendData ?? {}) as Partial<Record<keyof PositionPlanConfig, unknown>>
  return {
    riskType: raw.riskType === 'shares' ? 'shares' : DEFAULT_PLAN_CONFIG.riskType,
    riskValue:
      isNum(raw.riskValue) && raw.riskValue > 0 ? raw.riskValue : DEFAULT_PLAN_CONFIG.riskValue,
    maxPositionDollars:
      isNum(raw.maxPositionDollars) && raw.maxPositionDollars > 0 ? raw.maxPositionDollars : 0,
    weights: Array.isArray(raw.weights) ? raw.weights.filter((w): w is number => isNum(w)) : [],
    // Only an explicit false disables — legacy payloads (field absent) keep
    // the pills, matching every other defaults-on-garbage rule here.
    showAxisLabels: raw.showAxisLabels !== false,
  }
}

export interface PlanShape {
  entries: PlanEntry[]
  stop: number
}

/** The `plan:weightEdit` event payload — a click on an entry's label pill.
 *  Carries the FULL config so the menu can write back complete extendData. */
export interface PlanWeightEditRequest {
  overlayId: string
  entryIndex: number
  entryCount: number
  config: PositionPlanConfig
  entryPrice: number
  /** Physical-aligned display numbers (entryDisplayNumbers) for the cascade. */
  displayNumbers: number[]
  /** Click x in main-pane widget coordinates — the menu anchors under the
   *  clicked pill (labels ride the plan's left edge, not the pane's). */
  x?: number
}

/** The preset splits offered by both the drawer and the on-chart % menu. */
export const PLAN_WEIGHT_PCT_OPTIONS = [10, 20, 25, 30, 33, 50, 66, 75, 100]

/** Entry point-indices in entry order: 0 first, then every index past the stop. */
export function entryPointIndices(pointCount: number): number[] {
  const out = [0]
  for (let i = 2; i < pointCount; i++) out.push(i)
  return out
}

/**
 * Points + config → the plan's logical shape, or null until the stop exists
 * (mid-gesture, after only the first click). Weights align with entry order;
 * missing slots fall back to an equal split.
 */
export function planFromPoints(
  points: Array<{ value?: number }>,
  config: PositionPlanConfig,
): PlanShape | null {
  if (points.length < 2 || !isNum(points[1]?.value)) return null
  const stop = points[1].value as number
  const indices = entryPointIndices(points.length)
  const equal = 100 / indices.length
  const entries: PlanEntry[] = []
  for (let e = 0; e < indices.length; e++) {
    const price = points[indices[e]]?.value
    if (!isNum(price)) return null
    const w = config.weights[e]
    entries.push({ price, weightPct: isNum(w) && w > 0 ? w : equal })
  }
  return { entries, stop }
}

export interface PlanPoint {
  timestamp: number
  value: number
}

/**
 * Append a new entry to a plan: seeded halfway between the LAST entry and the
 * stop (sharing the stop's timestamp — lines ray from the plan's LEFTMOST
 * point, so a seed at the stop's x never moves the drawing's left edge),
 * with allocations reset to an equal split (a new leg invalidates whatever
 * split the old legs had). Null when the plan is mid-gesture (no stop yet) or
 * already at the entry cap. Shared by the settings drawer's Add entry button
 * and the on-chart `plan_add_entry` pill (ChartManager onClick).
 */
export function appendPlanEntry(
  points: PlanPoint[],
  config: PositionPlanConfig,
): { points: PlanPoint[]; config: PositionPlanConfig } | null {
  if (points.length < 2 || points.length - 1 >= MAX_PLAN_ENTRIES) return null
  const stop = points[1]
  const lastEntry = points[points.length === 2 ? 0 : points.length - 1]
  if (!isNum(stop?.value) || !isNum(lastEntry?.value)) return null
  const nextPoints = [
    ...points,
    { timestamp: stop.timestamp, value: (lastEntry.value + stop.value) / 2 },
  ]
  const entryCount = nextPoints.length - 1
  return {
    points: nextPoints,
    // Exact-100 equal split (largest remainder): `fill(round(100/n))` summed
    // to 99 for n=3, which the sizing layer would now read as a 99% partial
    // plan. First (100 mod n) slots take the extra point.
    config: {
      ...config,
      weights: Array.from({ length: entryCount }, (_, i) =>
        i < 100 % entryCount ? Math.floor(100 / entryCount) + 1 : Math.floor(100 / entryCount),
      ),
    },
  }
}

/**
 * Remove one entry leg (by PHYSICAL entry index). The stop must stay at point
 * index 1, so the array is rebuilt as [firstEntry, stop, ...restEntries]; the
 * removed leg's allocation leaves WITH it — survivors keep their weights
 * verbatim (never-inflate; the plan just deploys less), except a lone
 * survivor which resets to 100. Null when it's the only entry — a one-leg
 * plan minus its leg is just "delete the drawing", which has its own flow.
 */
export function removePlanEntry(
  points: PlanPoint[],
  config: PositionPlanConfig,
  entryIndex: number,
): { points: PlanPoint[]; config: PositionPlanConfig } | null {
  const indices = entryPointIndices(points.length)
  if (indices.length <= 1 || entryIndex < 0 || entryIndex >= indices.length) return null
  const stop = points[1]
  if (!stop) return null
  const survivors = indices.filter((_, e) => e !== entryIndex).map((i) => points[i])
  const equal = 100 / indices.length
  // Never-inflate, same rule as the cascade: removing a leg removes ITS
  // allocation — survivors keep their weights verbatim and the plan simply
  // deploys less (the stop label's alloc % makes the shortfall legible).
  // Exception: a lone survivor resets to 100 — single-leg plans are always
  // fully allocated (cascadeWeights' n===1 invariant) and have no % editor a
  // stranded partial weight could be fixed with.
  const survivorWeights = indices
    .map((_, e) =>
      Math.round(isNum(config.weights[e]) && config.weights[e] > 0 ? config.weights[e] : equal),
    )
    .filter((_, e) => e !== entryIndex)

  return {
    points: [survivors[0], stop, ...survivors.slice(1)],
    config: { ...config, weights: survivorWeights.length === 1 ? [100] : survivorWeights },
  }
}

/**
 * The CASCADE model (Chris, 2026-08-25; under-allocation 2026-08-29):
 * allocations lock from E1 down. Editing entry k leaves every EARLIER display
 * number untouched and clamps the pick to the remaining budget (100 minus the
 * locked earlier weights). LATER entries are never inflated — they keep their
 * values, shrinking proportionally (largest-remainder) only when the new pick
 * leaves them less room than they currently hold. Every entry — including the
 * last — honors the user's pick, so the total may deliberately land UNDER
 * 100 (a partially-allocated plan); it can never exceed 100. The sizing layer
 * (`computePlanSizing`) scales deployed risk by the allocated fraction.
 *
 * `numbers` is the physical-aligned display numbering (entryDisplayNumbers);
 * `index` is the PHYSICAL entry being edited.
 */
export function cascadeWeights(
  current: number[],
  numbers: number[],
  index: number,
  pct: number,
): number[] {
  const n = current.length
  if (n === 0) return []
  if (n === 1) return [100]
  const clean = (v: number) => (isNum(v) && v > 0 ? Math.round(v) : 0)
  const myNum = numbers[index]
  const earlier = current.map((_, j) => j).filter((j) => numbers[j] < myNum)
  const later = current.map((_, j) => j).filter((j) => numbers[j] > myNum)

  const budget = Math.max(0, 100 - earlier.reduce((a, j) => a + clean(current[j]), 0))
  const target = Math.min(budget, Math.max(0, Math.round(pct)))
  const rest = budget - target

  const out = current.map(clean)
  out[index] = target

  // Later entries shrink into `rest` only when they overflow it; a pick that
  // leaves them room changes nothing (no silent inflation — a deliberate
  // under-allocation survives edits to earlier entries).
  const laterWeights = later.map((j) => clean(current[j]))
  const laterSum = laterWeights.reduce((a, b) => a + b, 0)
  if (laterSum > rest) {
    const shares = later.map((_, k) => (laterWeights[k] / laterSum) * rest)
    const floored = shares.map(Math.floor)
    let leftover = rest - floored.reduce((a, b) => a + b, 0)
    const byRemainder = shares
      .map((v, k) => ({ k, frac: v - Math.floor(v) }))
      .sort((a, b) => b.frac - a.frac)
    for (const { k } of byRemainder) {
      if (leftover <= 0) break
      floored[k] += 1
      leftover -= 1
    }
    later.forEach((j, k) => {
      out[j] = floored[k]
    })
  }
  return out
}

/**
 * Direction-aware display numbers, per physical entry: E1 is always the entry
 * FARTHEST from the stop (the first fill of a scale-in — you average TOWARD
 * your stop), counting up as entries near the stop. Flipping the plan (stop
 * dragged to the other side) reverses the numbering automatically. Weights
 * stay bound to their physical lines; only the labels renumber.
 */
export function entryDisplayNumbers(entryPrices: number[], stop: number): number[] {
  const order = entryPrices
    .map((price, i) => ({ i, d: Math.abs(price - stop) }))
    .sort((a, b) => b.d - a.d)
  const numbers = new Array<number>(entryPrices.length)
  order.forEach((o, rank) => {
    numbers[o.i] = rank + 1
  })
  return numbers
}

/* ------------------------------------------------------------------ */
/*  Labels                                                              */
/* ------------------------------------------------------------------ */

function fmtPrice(v: number, precision: number): string {
  return v.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  })
}

function fmtMoney(v: number): string {
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export interface PlanLabels {
  /** Aligned with entry order. */
  entries: string[]
  /** Present only for multi-entry plans. */
  avg: string | null
  stop: string
}

const INVALID_TEXT: Record<string, string> = {
  'stop-through-entries': 'stop through entries',
  'too-close': 'too close',
  'no-entries': 'no entries',
  'bad-input': 'check settings',
}

/**
 * The left-edge label text for every line. Totals ride the STOP label (that is
 * the line a trader reads risk off); per-entry labels carry allocation % and
 * the share slice once the plan has more than one entry.
 *
 * `showPrices` (default true): whether labels restate each line's price. The
 * overlay passes false while the y-axis price pills are on — the axis owns
 * the price then, and repeating it on the left is noise. Toggling the pills
 * off brings the prices back. The avg text always keeps its numbers (the avg
 * line has no axis pill).
 */
export function planLineLabels(
  plan: PlanShape,
  sizing: PlanSizing,
  precision: number,
  opts?: { showPrices?: boolean },
): PlanLabels {
  const showPrices = opts?.showPrices !== false
  const multi = plan.entries.length > 1
  const stopPrice = fmtPrice(plan.stop, precision)
  const withPrice = (base: string, price: string) => (showPrices ? `${base} ${price}` : base)

  // Direction-aware numbering: E1 = farthest from the stop, so a flipped plan
  // renumbers automatically.
  const numbers = entryDisplayNumbers(
    plan.entries.map((e) => e.price),
    plan.stop,
  )

  if (sizing.state !== 'ok') {
    return {
      entries: plan.entries.map((e, i) =>
        withPrice(multi ? `E${numbers[i]}` : 'Entry', fmtPrice(e.price, precision)),
      ),
      avg: null,
      stop: `${withPrice('Stop', stopPrice)} · ${INVALID_TEXT[sizing.reason]}`,
    }
  }

  // Compact multi-entry form (E1/E2, per Chris). Every pill carries the
  // chevron and opens the % menu — the last display entry included (its pick
  // may leave the plan deliberately under 100% — see cascadeWeights).
  const entries = plan.entries.map((e, i) => {
    if (!multi) return withPrice('Entry', fmtPrice(e.price, precision))
    const pct = Math.round(e.weightPct)
    const slice = sizing.perEntryShares[i].toLocaleString('en-US')
    return `${withPrice(`E${numbers[i]} (${pct}%)`, fmtPrice(e.price, precision))} · ${slice} sh ▾`
  })
  const stop =
    `${withPrice('Stop', stopPrice)} · ${sizing.sharesTotal.toLocaleString('en-US')} sh` +
    ` · $${fmtMoney(sizing.riskDollars)} risk` +
    (sizing.capped ? ' · capped' : '') +
    // Partial plan: the risk figure above is already scaled to the deployed
    // size — flag the allocation so the shortfall is legible at a glance.
    (sizing.allocPct < 100 ? ` · ${sizing.allocPct}% alloc` : '')
  // "Stop" here is the DISTANCE from the blended entry — dollars away and
  // percent away — not a restated price (the stop line already shows its own).
  const stopDist = Math.abs(sizing.avgEntry - plan.stop)
  const stopPct = Math.round((stopDist / sizing.avgEntry) * 100)
  return {
    entries,
    avg: multi
      ? `Avg: $${fmtPrice(sizing.avgEntry, precision)} | Stop: $${fmtPrice(stopDist, precision)} (${stopPct}%)`
      : null,
    stop,
  }
}

/* ------------------------------------------------------------------ */
/*  Figures                                                             */
/* ------------------------------------------------------------------ */

const LONG_COLOR = '#089981'
const SHORT_COLOR = '#f23645'
// Amber, not red: SHORT_COLOR already owns red for short entries, and a
// same-color stop made short plans unreadable (entry vs stop). Amber marks
// the risk line in BOTH directions (Chris 2026-08-30).
const STOP_COLOR = '#f59e0b'
const RISK_FILL = 'rgba(242, 54, 69, 0.10)'
const NEUTRAL_PILL = '#363a45'
/** Shared translucent capsule fill — chart-dark at 75% so the pill reads over
 *  candles without becoming a solid block. */
// On-chart labels sit on an OPAQUE canvas-colored backing (Chris 2026-08-30):
// its one job is masking candles/grid behind the text so the accent-outlined
// pill reads cleanly. Resolved from the theme token so light canvases get a
// light backing; cached briefly because this runs on every overlay paint.
// (A borderless variant was tried the same day and rejected - the outline
// stays.)
let pillBgCache: { value: string; at: number } | null = null
function pillBg(): string {
  const now = Date.now()
  if (pillBgCache && now - pillBgCache.at < 1000) return pillBgCache.value
  let value: string = CHART_COLORS.canvas
  if (typeof document !== 'undefined') {
    const token = getComputedStyle(document.documentElement)
      .getPropertyValue('--chart-canvas')
      .trim()
    if (token) value = `hsl(${token})`
  }
  pillBgCache = { value, at: now }
  return value
}
const MUTED_COLOR = '#787b86'
// Tuned live with Chris across three rounds: 1.0 competed with the draggable
// lines, 0.45 vanished, 0.7 still read too soft — 0.85 is the keeper.
const AVG_LINE_COLOR = 'rgba(120, 124, 134, 0.85)'
const LABEL_X = 8

/** Pill text size and horizontal padding — must stay in sync with `pill()`'s
 *  style block below, since the ✕ placement is derived from them. */
const PILL_FONT_SIZE = 11
const PILL_PAD_X = 5
/** Gap between the label pill and each inline chip (+ / ×) — wide enough
 *  that additive and destructive neighbors don't fat-finger. */
const PLUS_GAP = 6
/** Chip box edge — square, and exactly the label pills' height
 *  (PILL_FONT_SIZE + 3px padding top and bottom). */
const CHIP_BOX = PILL_FONT_SIZE + 6

/** Font string klinecharts' own text renderer builds for a pill of this size
 *  (`createFont`: weight, size, family, defaulting to Helvetica Neue). Matching
 *  it is what makes the measurement below agree with what gets painted. */
const PILL_FONT = `normal ${PILL_FONT_SIZE}px Helvetica Neue`

/** Lazily-built measuring context. `null` once we know none is available
 *  (jsdom returns null from getContext), so we stop retrying per figure. */
let measureCtx: CanvasRenderingContext2D | null | undefined

function measureCtxOrNull(): CanvasRenderingContext2D | null {
  if (measureCtx !== undefined) return measureCtx
  try {
    measureCtx = document.createElement('canvas').getContext('2d')
  } catch {
    measureCtx = null
  }
  return measureCtx
}

/** Test seam: forget the cached context so a test can swap the environment. */
export function resetPillMeasureCache(): void {
  measureCtx = undefined
}

/**
 * Pixel width of a label pill, used to place the ✕ that follows it.
 *
 * Measured, not guessed. The old `text.length * 6.1` approximation used a
 * wide-glyph average, so it ran long on narrow-glyph labels (spaces, `·`, `.`,
 * `1`, parens) — exactly the shape of an entry label like
 * `E1 (50%) 1.41 · 58 sh ▾` — and that overshoot is what left a visible gap
 * before the ✕.
 *
 * We measure here rather than calling klinecharts' `utils.calcTextWidth`
 * because that named export resolves to `undefined` under Vitest: the package's
 * `index.cjs` is a shim that does `module.exports = require('./umd/...')`, and
 * the ESM-interop layer cannot bind named exports through that reassignment.
 * It works in the browser (Vite takes the `module` field) but would silently
 * fall back in every test, so the behaviour could never be pinned.
 *
 * The arithmetic fallback still matters: jsdom has no canvas 2d context.
 */
export function estimatePillWidth(text: string): number {
  const ctx = measureCtxOrNull()
  if (ctx) {
    ctx.font = PILL_FONT
    const measured = ctx.measureText(text).width
    if (measured > 0) return Math.round(measured) + PILL_PAD_X * 2
  }
  return Math.round(text.length * 6.1) + 10
}

/**
 * Order-entry style capsule (Chris, 2026-08-29, after TradingView's order
 * pills): a thin border and text in the LINE's color over a translucent
 * chart-dark fill, instead of a solid color block with white text. Softer on
 * the eyes, and each pill still color-codes to its line. `accent` is that
 * border/text color; the fill is shared and just keeps the label readable
 * over candles.
 */
function pill(
  x: number,
  y: number,
  text: string,
  accent: string,
  opts?: { key?: string; interactive?: boolean },
): OverlayFigure {
  return {
    key: opts?.key ?? 'plan_label',
    type: 'text',
    ...(opts?.interactive ? {} : { ignoreEvent: true }),
    attrs: { x, y, text, baseline: 'middle', align: 'left' },
    styles: {
      style: 'stroke_fill',
      color: accent,
      size: PILL_FONT_SIZE,
      backgroundColor: pillBg(),
      borderColor: accent,
      borderSize: 1,
      borderRadius: 4,
      paddingLeft: PILL_PAD_X,
      paddingRight: PILL_PAD_X,
      paddingTop: 3,
      paddingBottom: 3,
    },
  }
}

/** A square icon chip (see iconChipFigure) sharing the row's centerline. */
function iconChip(x: number, y: number, paths: string[], key: string): OverlayFigure {
  return {
    key,
    type: ICON_CHIP_FIGURE,
    attrs: { x, y, size: CHIP_BOX, paths },
    styles: { color: MUTED_COLOR, backgroundColor: pillBg(), borderRadius: 4 },
  } as OverlayFigure
}

function planRayLine(
  key: string,
  y: number,
  fromX: number,
  paneWidth: number,
  color: string,
  dashed: boolean,
  ignoreEvent = false,
  size = 1,
): OverlayFigure {
  return {
    key,
    type: 'line',
    ...(ignoreEvent ? { ignoreEvent: true } : {}),
    attrs: {
      coordinates: [
        { x: fromX, y },
        { x: paneWidth, y },
      ],
    },
    styles: {
      style: dashed ? 'dashed' : 'solid',
      dashedValue: dashed ? [4, 4] : undefined,
      color,
      size,
    },
  }
}

export interface PlanFigureInput {
  points: Array<{ value?: number }>
  coords: Array<{ x: number; y: number }>
  paneWidth: number
  precision: number
  config: PositionPlanConfig
  /** Price → pixel y on the pane (yAxis.convertToPixel). Null when off-scale. */
  priceToY: (price: number) => number | null
  /** Whole-plan drag mode: when the overlay is SELECTED, every line becomes a
   *  non-anchor figure so any drag translates the whole plan; unselected,
   *  lines are anchor-keyed and drag individually. */
  selected?: boolean
}

/** Pure figure builder for the plan — lines, labels, avg line, risk zone. */
export function buildPlanFigures(input: PlanFigureInput): OverlayFigure[] {
  const { points, coords, paneWidth, precision, config, priceToY } = input
  if (points.length === 0 || coords.length === 0) return []

  const plan = planFromPoints(points, config)

  // Left edge of the whole drawing = the LEFTMOST click's x (Chris,
  // 2026-08-29): lines ray from there to the pane's right edge instead of
  // spanning the full width. Labels clamp to the pane so they stay readable
  // when the anchor scrolls off-screen left.
  const xs = coords.map((c) => c?.x).filter(isNum)
  const leftX = xs.length > 0 ? Math.min(...xs) : 0
  const labelX = Math.max(leftX, 0) + LABEL_X
  // While the y-axis pills carry each line's price, the left labels drop the
  // restated price; toggling the pills off brings it back.
  const showPrices = config.showAxisLabels === false

  // Mid-gesture: only the first click exists — draw the entry line so the
  // placement feels alive before the stop lands.
  if (!plan) {
    const y = coords[0]?.y
    const price = points[0]?.value
    if (!isNum(y) || !isNum(price)) return []
    return [
      planRayLine('anchor_0', y, leftX, paneWidth, MUTED_COLOR, false),
      pill(labelX, y, showPrices ? `Entry ${fmtPrice(price, precision)}` : 'Entry', MUTED_COLOR),
    ]
  }

  const sizing = computePlanSizing({
    entries: plan.entries,
    stop: plan.stop,
    riskType: config.riskType,
    riskValue: config.riskValue,
    maxPositionDollars: config.maxPositionDollars,
    tick: Math.pow(10, -precision),
  })
  const ok = sizing.state === 'ok'
  const accent = ok ? (sizing.direction === 'long' ? LONG_COLOR : SHORT_COLOR) : MUTED_COLOR
  const labels = planLineLabels(plan, sizing, precision, { showPrices })

  const figures: OverlayFigure[] = []

  // Risk zone spanning the WHOLE plan — topmost line to bottommost line, so a
  // second entry widens it rather than shrinking it to avg→stop. Behind
  // everything, event-off.
  const stopY = coords[1]?.y
  if (ok && isNum(stopY)) {
    const ys = coords.map((c) => c?.y).filter(isNum)
    const top = Math.min(...ys)
    const bottom = Math.max(...ys)
    figures.push({
      key: 'plan_risk_zone',
      type: 'rect',
      ignoreEvent: true,
      attrs: { x: leftX, y: top, width: paneWidth - leftX, height: bottom - top },
      styles: { style: 'fill', color: RISK_FILL },
    })
  }

  // One draggable full-width line per point; entries in the direction accent,
  // stop dashed red.
  const indices = entryPointIndices(points.length)
  const multi = indices.length > 1
  // Selected = whole-plan drag: non-anchor keys route drags to translate, and
  // the thicker stroke is the "entire thing is active" cue.
  const selected = input.selected === true
  const lineKey = (pointIdx: number) => (selected ? `plan_line_${pointIdx}` : `anchor_${pointIdx}`)
  const lineSize = selected ? 2 : 1
  indices.forEach((pointIdx, entryIdx) => {
    const y = coords[pointIdx]?.y
    if (!isNum(y)) return
    figures.push(
      planRayLine(lineKey(pointIdx), y, leftX, paneWidth, accent, false, false, lineSize),
    )
    // EVERY multi-entry label is a CLICK TARGET (ChartManager routes the
    // figureKey to the on-chart menu) — the last display entry's menu offers
    // only Remove entry since its share is derived. A single entry has no
    // menu at all. Unselected, the key rides the `anchor_<pointIdx>` prefix so
    // DRAGGING a label moves its own line (the vendored patch prefix-matches);
    // selected, the plain key routes drags to whole-plan translate like the
    // lines.
    const label = labels.entries[entryIdx]
    figures.push(
      pill(
        labelX,
        y,
        label,
        ok ? accent : MUTED_COLOR,
        multi
          ? {
              key: selected ? `plan_weight_${entryIdx}` : `anchor_${pointIdx}__weight_${entryIdx}`,
              interactive: true,
            }
          : undefined,
      ),
    )
    // Inline chips after the label, EVERY row, additive before destructive:
    // [label] ＋ ✕. The ＋ (fullwidth, matching ✕'s optical size — ASCII +
    // renders tiny at pill scale) appends a leg via ChartManager's onClick
    // routing (hidden at the entry cap); the ✕ removes THIS leg (multi-entry
    // only). PLUS_GAP separates all three so neighbors don't fat-finger.
    let chipX = labelX + estimatePillWidth(label)
    if (points.length - 1 < MAX_PLAN_ENTRIES) {
      figures.push(iconChip(chipX + PLUS_GAP, y, LUCIDE_PLUS_PATHS, 'plan_add_entry'))
      chipX += PLUS_GAP + CHIP_BOX
    }
    if (multi) {
      figures.push(
        iconChip(
          chipX + PLUS_GAP,
          y,
          LUCIDE_X_PATHS,
          selected ? `plan_remove_${entryIdx}` : `anchor_${pointIdx}__remove_${entryIdx}`,
        ),
      )
    }
  })
  if (isNum(stopY)) {
    figures.push(
      planRayLine(lineKey(1), stopY, leftX, paneWidth, STOP_COLOR, true, false, lineSize),
    )
    figures.push(pill(labelX, stopY, labels.stop, ok ? STOP_COLOR : MUTED_COLOR))
  }

  // Avg entry — informational, deliberately FAINT (low-alpha dotted line,
  // bare muted text instead of a pill). It's derived context, and at full
  // strength it competed with the draggable lines it sits between.
  if (ok && labels.avg) {
    const avgY = priceToY(sizing.avgEntry)
    if (isNum(avgY)) {
      figures.push({
        key: 'plan_avg_entry',
        type: 'line',
        ignoreEvent: true,
        attrs: {
          coordinates: [
            { x: leftX, y: avgY },
            { x: paneWidth, y: avgY },
          ],
        },
        styles: { style: 'dashed', dashedValue: [2, 4], color: AVG_LINE_COLOR, size: 1 },
      })
      figures.push({
        key: 'plan_avg_label',
        type: 'text',
        ignoreEvent: true,
        attrs: { x: labelX, y: avgY - 8, text: labels.avg, baseline: 'middle', align: 'left' },
        // backgroundColor MUST be explicit: klinecharts' default text style
        // paints its accent blue behind any text figure that omits it.
        styles: { style: 'fill', color: AVG_LINE_COLOR, size: 10, backgroundColor: 'transparent' },
      })
    }
  }

  return figures
}

export interface PlanAxisFigureInput {
  points: Array<{ value?: number }>
  coords: Array<{ x: number; y: number }>
  precision: number
  config: PositionPlanConfig
}

/**
 * Per-line price pills for the RIGHT price axis — the same always-visible
 * treatment `horizontalLineOverlay` gives its level (whose pure pill builder
 * this reuses). One pill per plan line: entries in the direction accent, the
 * stop in red; deactivated wholesale by `config.showAxisLabels`.
 */
export function buildPlanAxisFigures(input: PlanAxisFigureInput): OverlayFigure[] {
  const { points, coords, precision, config } = input
  if (config.showAxisLabels === false) return []
  if (points.length === 0 || coords.length === 0) return []

  const pillAt = (key: string, y: number | undefined, price: number | undefined, bg: string) => {
    if (!isNum(y) || !isNum(price)) return null
    const fig = buildHorizontalLineAxisFigures(y, fmtPrice(price, precision), bg)[0]
    return fig ? ({ ...fig, key } as OverlayFigure) : null
  }

  // Mid-gesture: only the first click exists — a neutral pill tracks it.
  const stopPrice = points[1]?.value
  if (points.length < 2 || !isNum(stopPrice)) {
    const fig = pillAt('plan_axis_entry_0', coords[0]?.y, points[0]?.value, NEUTRAL_PILL)
    return fig ? [fig] : []
  }

  // Direction straight from the geometry (mirrors computePlanSizing): a stop
  // strictly on one side of every entry gets the long/short accent; an
  // incoherent plan keeps neutral entry pills (prices still legible).
  const entryPrices = entryPointIndices(points.length)
    .map((i) => points[i]?.value)
    .filter(isNum)
  const allAbove = entryPrices.every((p) => p > stopPrice)
  const allBelow = entryPrices.every((p) => p < stopPrice)
  const accent = allAbove ? LONG_COLOR : allBelow ? SHORT_COLOR : NEUTRAL_PILL

  const figures: OverlayFigure[] = []
  entryPointIndices(points.length).forEach((pointIdx, entryIdx) => {
    const fig = pillAt(
      `plan_axis_entry_${entryIdx}`,
      coords[pointIdx]?.y,
      points[pointIdx]?.value,
      accent,
    )
    if (fig) figures.push(fig)
  })
  const stopFig = pillAt('plan_axis_stop', coords[1]?.y, stopPrice, STOP_COLOR)
  if (stopFig) figures.push(stopFig)
  return figures
}

/** Register the position-plan overlay with klinecharts (once). */
export const registerPositionPlanOverlay = registerOnce(() => {
  // The chips render through the custom vector figure — must exist first.
  registerIconChipFigure()
  registerOverlay({
    name: POSITION_PLAN_NAME,
    totalStep: 3,
    // Same contract as riskRewardOverlay: anchor-keyed CUSTOM figures are the
    // drag handles; klinecharts' own point dots would be redundant clutter.
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    // Selected-only default pills are replaced by the always-visible custom
    // axis pills below (createYAxisFigures), gated on config.showAxisLabels.
    needDefaultYAxisFigure: false,
    // Vertical-only per-line drags. Since the lines ray from the plan's
    // leftmost point, x is MEANINGFUL — but klinecharts' default point move
    // writes both axes, so dragging a line/label horizontally shifted the
    // drawing's left edge. This hook (invoked by eventPressedPointMove AFTER
    // the default write, with `this` bound to the OverlayImp) restores the
    // dragged point's pre-drag x from the mousedown snapshot and keeps only
    // the price change. Whole-plan translate (selected mode) takes the
    // eventPressedOtherMove path, which never calls this — moving the whole
    // tool after click-to-select still works on both axes.
    // NOT an arrow function: `this` must bind to the overlay instance;
    // `_prevPressedPoints` is klinecharts-private, tolerable on a vendored,
    // patched copy (guarded so absence degrades to today's behavior).
    performEventPressedMove(
      this: { _prevPressedPoints?: Array<{ timestamp?: number; dataIndex?: number }> | null },
      {
        points,
        performPointIndex,
      }: {
        points: Array<{ timestamp?: number; dataIndex?: number; value?: number }>
        performPointIndex: number
      },
    ) {
      const prev = this?._prevPressedPoints?.[performPointIndex]
      const p = points[performPointIndex]
      if (prev && p) {
        p.timestamp = prev.timestamp
        p.dataIndex = prev.dataIndex
      }
    },
    createYAxisFigures: ({ coordinates, overlay, precision, yAxis }) => {
      return buildPlanAxisFigures({
        points: (overlay.points ?? []) as Array<{ value?: number }>,
        coords: coordinates as Array<{ x: number; y: number }>,
        precision: resolvePricePrecision(yAxis, precision),
        config: parsePlanConfig((overlay as { extendData?: unknown }).extendData),
      })
    },
    createPointFigures: ({ coordinates, overlay, bounding, precision, yAxis }) => {
      return buildPlanFigures({
        points: (overlay.points ?? []) as Array<{ value?: number }>,
        coords: coordinates as Array<{ x: number; y: number }>,
        paneWidth: bounding.width,
        precision: resolvePricePrecision(yAxis, precision),
        config: parsePlanConfig((overlay as { extendData?: unknown }).extendData),
        priceToY: (price: number) => {
          const y = (yAxis as { convertToPixel?: (v: number) => number } | null)?.convertToPixel?.(
            price,
          )
          return isNum(y) ? y : null
        },
        selected: isPlanSelected(overlay.id),
      })
    },
  } as OverlayTemplateParam)
})
